import { useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut, Bell, Settings, Menu, Sun, Moon, SquareArrowOutUpRight, Inbox } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useSidebar } from '@/contexts/SidebarContext';
import { hasHrPages } from '@/components/Sidebar';
import { usePendingApprovalCount, PENDING_REQUESTS_PATH } from '@/hooks/usePendingApprovalCount';
import { getMyNotifications, markAllNotificationsRead, markNotificationRead } from '@/api/notifications';
import { isIndependenceWindow } from '@/utils/festive';
import { TricolorRing } from '@/components/TricolorRing';
import SosWidget from '@/components/andbook/SosWidget';

interface TopbarProps {
  sidebarWidth: number;
  isMobile: boolean;
  onHamburgerClick: () => void;
}


function Avatar({ name, photoUrl, size = 36, fontSize = 13 }: {
  name: string | null | undefined;
  photoUrl: string | null;
  size?: number;
  fontSize?: number;
}) {
  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() ?? '?';

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name ?? ''}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
      />
    );
  }

  return (
    <div
      className="flex items-center justify-center rounded-full font-bold flex-shrink-0"
      style={{ width: size, height: size, background: 'var(--sidebar-bg)', color: 'var(--brand-orange)', fontSize }}
    >
      {initials}
    </div>
  );
}

export function Topbar({ sidebarWidth, isMobile, onHamburgerClick }: TopbarProps): JSX.Element {
  const { user, logout, photoUrl } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { dark, toggle: toggleDark } = useDarkMode();

  const isAndBook = location.pathname.startsWith('/andbook');

  // HR View — a quick action that swaps the sidebar over to the HR pages. It is
  // offered to whoever actually has HR pages to see, not to a named role.
  const { navView, setNavView } = useSidebar();
  const hrViewOn = navView === 'hr';
  const showHrViewButton = hasHrPages(user);
  // Requests waiting on this user's approval — the same count as the sidebar.
  const pendingApprovalCount = usePendingApprovalCount();
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  // Scope the bell to the app you're in: AndBook pages show AndBook notifications,
  // everywhere else shows core (AndOne) notifications.
  const notifScope = isAndBook ? 'andbook' as const : 'core' as const;
  const { data: notifData } = useQuery({
    queryKey: ['notifications', notifScope],
    queryFn: () => getMyNotifications(undefined, notifScope),
    refetchInterval: 30_000,
    retry: false,
  });
  const unreadCount = notifData?.unreadCount ?? 0;
  const recentNotifs = (notifData?.notifications ?? []).slice(0, 8);

  const displayName = user?.msDisplayName ?? user?.name;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setShowPopup(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifDropdown(false);
      }
    }
    if (showPopup || showNotifDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopup, showNotifDropdown]);

  return (
    <header
      className="fixed z-30 flex items-center"
      style={{
        top: 0,
        right: 0,
        left: sidebarWidth,
        height: 'var(--topbar-height)',
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        borderLeft: '1px solid var(--color-border)',
        transition: 'left 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'left',
        gap: isMobile ? 8 : 16,
        paddingLeft: isMobile ? 16 : 24,
        paddingRight: isMobile ? 16 : 16,
      }}
    >
      {/* Hamburger — mobile only */}
      {isMobile && (
        <button
          type="button"
          onClick={onHamburgerClick}
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 40, height: 40,
            borderRadius: 8,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <Menu style={{ width: 22, height: 22, color: 'var(--brand-navy)' }} />
        </button>
      )}

      {/* App name */}
      <button
        type="button"
        onClick={() => navigate(isAndBook ? '/andbook/dashboard' : user?.roles?.includes('Employee') ? '/andone/dashboard' : '/andone/admin/dashboard')}
        style={{ fontSize: isMobile ? 18 : 21, fontWeight: 800, color: 'var(--brand-navy)', letterSpacing: '-0.3px', userSelect: 'none', flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        And<span style={{ color: 'var(--brand-orange)' }}>{isAndBook ? 'Book' : 'One'}</span>
        {isIndependenceWindow() && <span title="Happy Independence Day" style={{ marginLeft: 7, fontSize: isMobile ? 22 : 28, verticalAlign: '-2px' }}>🇮🇳</span>}
      </button>

      {/* Approvals waiting on this user — straight to the Pending requests. */}
      {!isAndBook && pendingApprovalCount > 0 && (
        <button
          type="button"
          onClick={() => navigate(PENDING_REQUESTS_PATH)}
          title={`${pendingApprovalCount} request${pendingApprovalCount !== 1 ? 's' : ''} waiting for your approval`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            marginLeft: isMobile ? 8 : 14, flexShrink: 0,
            padding: isMobile ? '4px 9px' : '5px 12px', borderRadius: 20,
            fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
            color: 'var(--brand-orange)',
            background: 'rgba(245,163,0,0.12)',
            border: '1px solid rgba(245,163,0,0.30)',
          }}
        >
          <Inbox size={14} />
          {pendingApprovalCount > 99 ? '99+' : pendingApprovalCount}
          {!isMobile && <span style={{ fontWeight: 600 }}>pending</span>}
        </button>
      )}

      <div className="flex-1" />

      {/* Admin pill — visible only for admins */}
      {!isMobile && user?.roles?.includes('Admin') && (
        <span style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: 'var(--brand-orange)',
          background: 'rgba(245,163,0,0.12)',
          border: '1px solid rgba(245,163,0,0.22)',
          padding: '3px 10px',
          borderRadius: 20,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          Admin
        </span>
      )}

      {/* HR View — only outside AndBook, whose nav has its own admin section.
          On, the sidebar lists the HR pages instead of the employee ones;
          pressing it again hands the employee routes back. */}
      {showHrViewButton && !isAndBook && (
        <button
          type="button"
          onClick={() => setNavView(hrViewOn ? 'employee' : 'hr')}
          title={hrViewOn ? 'Back to my pages' : 'Show the HR pages'}
          aria-pressed={hrViewOn}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0,
            padding: '5px 12px', borderRadius: 20, cursor: 'pointer',
            fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap',
            border: `1px solid ${hrViewOn ? 'var(--brand-navy)' : 'var(--color-border)'}`,
            background: hrViewOn ? 'var(--brand-navy)' : 'var(--color-bg)',
            color: hrViewOn ? '#fff' : 'var(--color-text-muted)',
            transition: 'all 0.15s',
          }}
        >
          <span style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
            background: hrViewOn ? '#4ADE80' : 'var(--color-text-subtle)',
          }} />
          HR View
        </button>
      )}

      {/* AndBook SOS — only on AndBook routes, only while the user has an active booking */}
      {isAndBook && <SosWidget compact={isMobile} />}

      {/* AndHub launcher — jumps back to the apps grid at /home */}
      <button
        type="button"
        onClick={() => navigate('/home')}
        title="Switch to AndHub apps launcher"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: isMobile ? 12.5 : 13.5, fontWeight: 700,
          color: 'var(--brand-navy)',
          background: 'rgba(245, 163, 0, 0.12)',
          border: '1px solid rgba(245, 163, 0, 0.45)',
          borderRadius: 20,
          padding: isMobile ? '4px 10px' : '5px 13px',
          cursor: 'pointer',
          flexShrink: 0, whiteSpace: 'nowrap',
          transition: 'background 160ms ease, border-color 160ms ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(245, 163, 0, 0.22)';
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(245, 163, 0, 0.7)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(245, 163, 0, 0.12)';
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(245, 163, 0, 0.45)';
        }}
      >
        AndHub
        <SquareArrowOutUpRight size={12} style={{ color: 'var(--brand-orange)' }} />
      </button>

      {/* Dark mode toggle */}
      {!isAndBook && <button
        type="button"
        onClick={toggleDark}
        title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        className="relative flex items-center justify-center cursor-pointer flex-shrink-0"
        style={{
          width: 36, height: 36,
          borderRadius: '50%',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-border)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-bg)'; }}
      >
        {dark
          ? <Sun style={{ width: 16, height: 16, color: 'var(--brand-orange)' }} />
          : <Moon style={{ width: 16, height: 16, color: 'var(--brand-navy)' }} />
        }
      </button>}

      {/* Bell with notification dropdown (shown in AndOne and AndBook) */}
      {<div ref={notifRef} style={{ position: 'relative', flexShrink: 0 }}>
        <button
          type="button"
          data-tour="notifications"
          onClick={() => setShowNotifDropdown(v => !v)}
          title="Notifications"
          className="relative flex items-center justify-center cursor-pointer flex-shrink-0"
          style={{
            width: 36, height: 36,
            borderRadius: '50%',
            background: showNotifDropdown ? 'var(--color-border)' : 'var(--color-bg)',
            border: '1px solid var(--color-border)',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-border)'; }}
          onMouseLeave={(e) => { if (!showNotifDropdown) (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-bg)'; }}
        >
          <Bell style={{ width: 16, height: 16, color: 'var(--brand-navy)' }} />
          {unreadCount > 0 && (
            <span
              className="absolute flex items-center justify-center font-black"
              style={{
                top: -3, right: -3,
                width: 16, height: 16,
                background: 'var(--brand-orange)',
                color: '#fff',
                fontSize: 10,
                borderRadius: '50%',
                border: '2px solid var(--color-surface)',
                pointerEvents: 'none',
              }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {showNotifDropdown && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            width: 340, maxHeight: 480,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 50,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid var(--color-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Bell style={{ width: 14, height: 14, color: 'var(--color-text-muted)' }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)' }}>Notifications</span>
                {unreadCount > 0 && (
                  <span style={{
                    padding: '1px 7px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                    background: 'var(--brand-orange)', color: 'var(--brand-navy-dark)',
                  }}>
                    {unreadCount}
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    void markAllNotificationsRead(notifScope).then(() => {
                      void qc.invalidateQueries({ queryKey: ['notifications'] });
                    });
                  }}
                  style={{
                    fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Notification list */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {recentNotifs.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                  <Bell style={{ width: 24, height: 24, color: 'var(--color-border)', margin: '0 auto 8px' }} />
                  <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: 0 }}>No notifications</p>
                </div>
              ) : (
                recentNotifs.map(n => (
                  <div
                    key={n.id}
                    onClick={() => {
                      if (!n.isRead) {
                        void markNotificationRead(String(n.id)).then(() => {
                          void qc.invalidateQueries({ queryKey: ['notifications'] });
                        });
                      }
                      setShowNotifDropdown(false);
                      if (n.type.startsWith('andbook')) {
                        if (n.type === 'andbook_booking_request') navigate('/andbook/approvals');
                        else if (n.type === 'andbook_sos') navigate('/andbook/sos');
                        else if (n.type === 'andbook_sos_update') navigate('/andbook/dashboard');
                        else navigate('/andbook/my-bookings');
                        return;
                      }
                      if (!n.relatedEntityId) return;
                      const id = n.relatedEntityId;
                      if (n.type === 'APPROVED' || n.type === 'REJECTED') {
                        navigate('/andone/my-requests', { state: { openRequestId: Number(id) } });
                      } else if (n.type === 'INFO') {
                        const isAdminOrHR = (user?.roles ?? []).some(r => r === 'Admin' || r === 'HR');
                        if (isAdminOrHR) navigate(`/andone/admin/requests?id=${id}`);
                      } else if (n.type === 'ABSENT_MARKED' || n.type === 'ABSENT') {
                        navigate('/andone/my-records');
                      }
                    }}
                    style={{
                      padding: '11px 16px',
                      borderBottom: '1px solid var(--color-border)',
                      background: n.isRead ? 'transparent' : 'var(--color-bg)',
                      cursor: (!n.isRead || (n.relatedEntityId && n.type !== 'PROFILE_UPDATED')) ? 'pointer' : 'default',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { if (n.relatedEntityId && n.type !== 'PROFILE_UPDATED') e.currentTarget.style.background = 'var(--color-bg)'; }}
                    onMouseLeave={e => { if (n.relatedEntityId && n.type !== 'PROFILE_UPDATED') e.currentTarget.style.background = n.isRead ? 'transparent' : 'var(--color-bg)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      {!n.isRead && (
                        <span style={{
                          width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 4,
                          background: 'var(--brand-orange)',
                        }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                          <p style={{
                            fontSize: 14, fontWeight: n.isRead ? 500 : 700,
                            color: 'var(--color-text)', margin: '0 0 2px',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {n.title}
                          </p>
                          {!!n.relatedEntityId && (n.type === 'APPROVED' || n.type === 'REJECTED' || n.type === 'INFO' || n.type === 'ABSENT_MARKED' || n.type === 'ABSENT') && (
                            <SquareArrowOutUpRight style={{ width: 11, height: 11, color: 'var(--color-text-muted)', flexShrink: 0, marginTop: 3 }} />
                          )}
                        </div>
                        <p style={{
                          fontSize: 13, color: 'var(--color-text-muted)', margin: '0 0 4px',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {n.body}
                        </p>
                        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: 0 }}>
                          {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <button
              type="button"
              onClick={() => {
                setShowNotifDropdown(false);
                void qc.invalidateQueries({ queryKey: ['notifications', 'list'] });
                navigate(isAndBook ? '/andbook/notifications' : '/andone/notifications');
              }}
              style={{
                padding: '11px 16px', background: 'var(--color-bg)',
                border: 'none', borderTop: '1px solid var(--color-border)',
                fontSize: 14, fontWeight: 600, color: 'var(--brand-navy)',
                cursor: 'pointer', width: '100%', textAlign: 'center',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-border)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-bg)'; }}
            >
              View all notifications
            </button>
          </div>
        )}
      </div>}

      {/* Avatar with popup */}
      {!isAndBook && <div ref={popupRef} style={{ position: 'relative', flexShrink: 0, marginRight: 12 }}>
        <button
          type="button"
          onClick={() => setShowPopup((prev) => !prev)}
          title="Account"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%', border: isIndependenceWindow() ? 'none' : '2px solid var(--brand-orange)',
            background: 'none', cursor: 'pointer', padding: 0,
            transition: 'border-color 0.15s',
          }}
        >
          <TricolorRing gapColor="var(--topbar-bg, #fff)">
            <Avatar name={displayName} photoUrl={photoUrl} />
          </TricolorRing>
        </button>

        {showPopup && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 8,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 200,
            overflow: 'hidden',
            zIndex: 50,
          }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border)' }}>
              <p style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 1px' }}>
                {displayName ?? 'User'}
              </p>
              {user?.designation && (
                <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', margin: '0 0 1px' }}>
                  {user.designation}
                </p>
              )}
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email ?? ''}
              </p>
            </div>

            <div style={{ padding: '6px 0' }}>
              {!isAndBook && (
                <>
                  <button
                    type="button"
                    onClick={() => { setShowPopup(false); navigate('/andone/profile'); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 16px', background: 'none', border: 'none',
                      cursor: 'pointer', fontSize: 14.5, fontWeight: 500, color: 'var(--color-text)',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-bg)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                  >
                    <User style={{ width: 16, height: 16, color: 'var(--color-text-muted)' }} />
                    My Profile
                  </button>

                  <button
                    type="button"
                    onClick={() => { setShowPopup(false); navigate('/andone/settings'); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 16px', background: 'none', border: 'none',
                      cursor: 'pointer', fontSize: 14.5, fontWeight: 500, color: 'var(--color-text)',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-bg)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                  >
                    <Settings style={{ width: 16, height: 16, color: 'var(--color-text-muted)' }} />
                    Settings
                  </button>
                </>
              )}

              <div style={{ height: 1, background: 'var(--color-border)', margin: '4px 0' }} />

              <button
                type="button"
                onClick={() => { setShowPopup(false); logout(); }}
                className="topbar-logout-hover"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 16px', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 14.5, fontWeight: 500, color: 'var(--color-danger-text)',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-danger-bg)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
              >
                <LogOut style={{ width: 16, height: 16 }} />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>}
    </header>
  );
}
